import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../store";
import { useAppSelector } from "../hook";

export type TmsAssetTreeKnownOptionIds = {
  branches: string[];
  segments: string[];
  clients: string[];
  suppliers: string[];
  vehicles: string[];
  drivers: string[];
};

export type TmsAssetTreeProps = {
  selectedBranches: string[];
  selectedSegments: string[];
  selectedFleetGroups: string[];
  selectedClients: string[];
  selectedSuppliers: string[];
  selectedVehicles: string[];
  selectedDrivers: string[];
  includeNullBranch: boolean;
  includeNullSegment: boolean;
  includeNullClient: boolean;
  includeNullSupplier: boolean;
  includeNullVehicle: boolean;
  includeNullDriver: boolean;
  assets?: any[];
  isTreeReady: boolean;
  knownOptionIds?: TmsAssetTreeKnownOptionIds;
};

const initialState: TmsAssetTreeProps = {
  selectedBranches: [],
  selectedSegments: [],
  selectedFleetGroups: [],
  selectedClients: [],
  selectedSuppliers: [],
  selectedVehicles: [],
  selectedDrivers: [],
  includeNullBranch: false,
  includeNullSegment: false,
  includeNullClient: false,
  includeNullSupplier: false,
  includeNullVehicle: false,
  includeNullDriver: false,
  assets: [],
  isTreeReady: false,
};

export const TmsAssetTreeSlice = createSlice({
  name: "tmsAssetTree",
  initialState,
  reducers: {
    setSelectedBranches: (state, action: PayloadAction<string[]>) => {
      state.selectedBranches = action.payload;
    },
    setSelectedSegments: (state, action: PayloadAction<string[]>) => {
      state.selectedSegments = action.payload;
    },
    setSelectedFleetGroups: (state, action: PayloadAction<string[]>) => {
      state.selectedFleetGroups = action.payload;
    },
    setSelectedClients: (state, action: PayloadAction<string[]>) => {
      state.selectedClients = action.payload;
    },
    setSelectedSuppliers: (state, action: PayloadAction<string[]>) => {
      state.selectedSuppliers = action.payload;
    },
    setSelectedVehicles: (state, action: PayloadAction<string[]>) => {
      state.selectedVehicles = action.payload;
    },
    setSelectedDrivers: (state, action: PayloadAction<string[]>) => {
      state.selectedDrivers = action.payload;
    },
    setIncludeNullBranch: (state, action: PayloadAction<boolean>) => {
      state.includeNullBranch = action.payload;
    },
    setIncludeNullSegment: (state, action: PayloadAction<boolean>) => {
      state.includeNullSegment = action.payload;
    },
    setIncludeNullClient: (state, action: PayloadAction<boolean>) => {
      state.includeNullClient = action.payload;
    },
    setIncludeNullSupplier: (state, action: PayloadAction<boolean>) => {
      state.includeNullSupplier = action.payload;
    },
    setIncludeNullVehicle: (state, action: PayloadAction<boolean>) => {
      state.includeNullVehicle = action.payload;
    },
    setIncludeNullDriver: (state, action: PayloadAction<boolean>) => {
      state.includeNullDriver = action.payload;
    },
    setAssets: (state, action: PayloadAction<any[]>) => {
      state.assets = action.payload;
    },
    setTreeReady: (state, action: PayloadAction<boolean>) => {
      state.isTreeReady = action.payload;
    },
    setKnownOptionIds: (state, action: PayloadAction<TmsAssetTreeKnownOptionIds>) => {
      state.knownOptionIds = action.payload;
    },
    clearAllFilters: (state) => {
      state.selectedBranches = [];
      state.selectedSegments = [];
      state.selectedFleetGroups = [];
      state.selectedClients = [];
      state.selectedSuppliers = [];
      state.selectedVehicles = [];
      state.selectedDrivers = [];
      state.includeNullBranch = false;
      state.includeNullSegment = false;
      state.includeNullClient = false;
      state.includeNullSupplier = false;
      state.includeNullVehicle = false;
      state.includeNullDriver = false;
    },
  },
});

export const {
  setSelectedBranches,
  setSelectedSegments,
  setSelectedFleetGroups,
  setSelectedClients,
  setSelectedSuppliers,
  setSelectedVehicles,
  setSelectedDrivers,
  setIncludeNullBranch,
  setIncludeNullSegment,
  setIncludeNullClient,
  setIncludeNullSupplier,
  setIncludeNullVehicle,
  setIncludeNullDriver,
  clearAllFilters,
  setAssets,
  setTreeReady,
  setKnownOptionIds,
} = TmsAssetTreeSlice.actions;

export const getStoredSelectedBranches = (state: RootState) => state.tmsAssetTree.selectedBranches;
export const getStoredSelectedSegments = (state: RootState) => state.tmsAssetTree.selectedSegments;
export const getStoredSelectedFleetGroups = (state: RootState) => state.tmsAssetTree.selectedFleetGroups;
export const getStoredSelectedClients = (state: RootState) => state.tmsAssetTree.selectedClients;
export const getStoredSelectedSuppliers = (state: RootState) => state.tmsAssetTree.selectedSuppliers;
export const getStoredSelectedVehicles = (state: RootState) => state.tmsAssetTree.selectedVehicles;
export const getStoredSelectedDrivers = (state: RootState) => state.tmsAssetTree.selectedDrivers;
export const getStoredIncludeNullBranch = (state: RootState) => state.tmsAssetTree.includeNullBranch;
export const getStoredIncludeNullSegment = (state: RootState) => state.tmsAssetTree.includeNullSegment;
export const getStoredIncludeNullClient = (state: RootState) => state.tmsAssetTree.includeNullClient;
export const getStoredIncludeNullSupplier = (state: RootState) => state.tmsAssetTree.includeNullSupplier;
export const getStoredIncludeNullVehicle = (state: RootState) => state.tmsAssetTree.includeNullVehicle;
export const getStoredIncludeNullDriver = (state: RootState) => state.tmsAssetTree.includeNullDriver;
export const getStoredAssets = (state: RootState) => state.tmsAssetTree.assets;
export const getIsTreeReady = (state: RootState) => state.tmsAssetTree.isTreeReady;
export const getStoredKnownOptionIds = (state: RootState) => state.tmsAssetTree.knownOptionIds;

export const useGetStoredSelectedBranches = () => useAppSelector(getStoredSelectedBranches);
export const useGetStoredSelectedSegments = () => useAppSelector(getStoredSelectedSegments);
export const useGetStoredSelectedFleetGroups = () => useAppSelector(getStoredSelectedFleetGroups);
export const useGetStoredSelectedClients = () => useAppSelector(getStoredSelectedClients);
export const useGetStoredSelectedSuppliers = () => useAppSelector(getStoredSelectedSuppliers);
export const useGetStoredSelectedVehicles = () => useAppSelector(getStoredSelectedVehicles);
export const useGetStoredSelectedDrivers = () => useAppSelector(getStoredSelectedDrivers);
export const useGetStoredIncludeNullBranch = () => useAppSelector(getStoredIncludeNullBranch);
export const useGetStoredIncludeNullSegment = () => useAppSelector(getStoredIncludeNullSegment);
export const useGetStoredIncludeNullClient = () => useAppSelector(getStoredIncludeNullClient);
export const useGetStoredIncludeNullSupplier = () => useAppSelector(getStoredIncludeNullSupplier);
export const useGetStoredIncludeNullVehicle = () => useAppSelector(getStoredIncludeNullVehicle);
export const useGetStoredIncludeNullDriver = () => useAppSelector(getStoredIncludeNullDriver);
export const useGetStoredAssets = () => useAppSelector(getStoredAssets);
export const useIsTreeReady = () => useAppSelector(getIsTreeReady);
export const useGetStoredKnownOptionIds = () => useAppSelector(getStoredKnownOptionIds);

export default TmsAssetTreeSlice.reducer;
